import { create } from "zustand"
import type { PlanDisplayData } from "@/lib/plan-display-data"

export interface CompareToast {
  id: number
  message: string
  variant: "default" | "warning"
}

interface CompareState {
  plans: PlanDisplayData[]
  toasts: CompareToast[]
  dismissed: boolean
  addPlan: (plan: PlanDisplayData) => void
  removePlan: (slug: string) => void
  clearPlans: () => void
  setDismissed: (value: boolean) => void
  dismissToast: (id: number) => void
}

let toastSeq = 0

function pushToast(
  set: (fn: (s: CompareState) => Partial<CompareState>) => void,
  message: string,
  variant: "default" | "warning" = "default"
) {
  const id = ++toastSeq
  set((s) => ({ toasts: [...s.toasts, { id, message, variant }] }))
  setTimeout(() => {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
  }, 2000)
}

export const useCompareStore = create<CompareState>((set, get) => ({
  plans: [],
  toasts: [],
  dismissed: false,

  addPlan(plan) {
    const { plans } = get()
    if (plans.find((p) => p.slug === plan.slug)) {
      get().removePlan(plan.slug)
      return
    }
    if (plans.length >= 3) {
      pushToast(set, "You can compare up to 3 plans at a time.", "warning")
      return
    }
    set((s) => ({ plans: [...s.plans, plan], dismissed: false }))
    pushToast(set, `${plan.name} added to compare.`)
  },

  removePlan(slug) {
    const plan = get().plans.find((p) => p.slug === slug)
    const remaining = get().plans.filter((p) => p.slug !== slug)
    set({ plans: remaining, dismissed: remaining.length === 0 })
    if (plan) pushToast(set, `${plan.name} removed from compare.`)
  },

  clearPlans() {
    set({ plans: [], dismissed: true })
  },

  setDismissed(value) {
    set({ dismissed: value })
  },

  dismissToast(id) {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
  },
}))
